/**
 * Receipt Printer Utility for Sellox POS
 * Popup window printing for 80mm thermal printers (XP-80C)
 * Format matches Venezuelan SENIAT fiscal printer output exactly
 * Uses HTML tables for guaranteed column alignment on thermal printers
 */
import { busToast } from './toastBus';
import { rasterRawActive, printHtmlRaster } from './rasterPrint';
import QRCodeLib from 'qrcode';
import { legacyPromoDeductBs, promoSavedBs } from './promoEngine';
import { curSym } from "./cur";

// Sync QR → inline SVG (thermal printers render vector edges crisply)
export function qrSvg(text, sizeMm = 22) {
  try {
    const qr = QRCodeLib.create(text, { errorCorrectionLevel: 'M' });
    const n = qr.modules.size, data = qr.modules.data;
    let path = '';
    for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) if (data[y * n + x]) path += `M${x} ${y}h1v1h-1z`;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${n} ${n}" width="${sizeMm}mm" height="${sizeMm}mm" shape-rendering="crispEdges"><rect width="100%" height="100%" fill="#fff"/><path d="${path}" fill="#000"/></svg>`;
  } catch { return ''; }
}

// Sync QR → PNG <img> (self-contained data URL). Use this for anything printed through the
// RAW raster path: Electron 22 / Chromium 108 (Win7 legacy build) drops a nested <svg> rendered
// inside a foreignObject-as-image, so the QR came out blank on those PCs (testnegocio 2026-10-30).
// A PNG <img> renders identically on every Chromium version and never taints the canvas.
export function qrPng(text, sizeMm = 22) {
  try {
    if (typeof document === 'undefined') return qrSvg(text, sizeMm);
    const qr = QRCodeLib.create(text, { errorCorrectionLevel: 'M' });
    const n = qr.modules.size, data = qr.modules.data;
    const quiet = 2, scale = 8, dim = (n + quiet * 2) * scale;
    const cv = document.createElement('canvas'); cv.width = cv.height = dim;
    const ctx = cv.getContext('2d');
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, dim, dim);
    ctx.fillStyle = '#000';
    for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) if (data[y * n + x]) ctx.fillRect((x + quiet) * scale, (y + quiet) * scale, scale, scale);
    return `<img src="${cv.toDataURL('image/png')}" style="width:${sizeMm}mm;height:${sizeMm}mm;display:inline-block" alt="QR"/>`;
  } catch { return ''; }
}

// 🎨 Design overrides (paper width + font size) from the doc-design settings
// 🧾 Receipt templates (owner 2026-11-01): fiscal (default, SENIAT 3-column) · compact · modern · senior · a2 (old a2/1SISTEMA text ticket)
export const RECEIPT_TEMPLATES = ['fiscal', 'compact', 'modern', 'senior', 'a2'];
export function receiptTemplate(settings) {
  const t = String(settings?.receipt_template || 'fiscal');
  return RECEIPT_TEMPLATES.includes(t) ? t : 'fiscal';
}
// Monospace templates print two-column rows as ONE pre-formatted text line (label + spaces + amount) instead of a <table>:
// Alfonzo's printer (2026-11-01 photo) drew the amount one line above its label on table rows — plain text cannot misalign.
const TEXT_TEMPLATES = ['compact', 'senior', 'a2'];
function templateCss(tpl) {
  if (tpl === 'compact') return 'body{font-size:11px;line-height:1.15}table.r td,table.r3 td,div.line{padding:0!important;line-height:1.15!important;font-size:11px}.sep{margin:1px 0}.xl{font-size:13px!important}';
  if (tpl === 'modern') return "body,table.r,table.r3,div.line{font-family:Arial,Helvetica,sans-serif!important}body{font-size:12px;line-height:1.35}.xl{font-size:19px!important;font-weight:900;letter-spacing:.5px}.lg{font-size:14px!important}.sep{border-top:1px dashed #555;margin:4px 0}.hd{font-weight:700;border-bottom:1px solid #000}.name{font-weight:700}.sub{color:#444;font-size:11px}";
  if (tpl === 'senior') return 'body{font-size:16px!important;line-height:1.4;font-weight:700}table.r td,table.r3 td,div.line{font-size:16px!important}.xl{font-size:22px!important}.lg{font-size:18px!important}.sep{border-top:2px solid #000;margin:4px 0}';
  if (tpl === 'a2') return 'body{font-size:11px;line-height:1.2}div.line,.sep{font-size:11px;line-height:1.2}.xl{font-size:12px!important}.lg{font-size:11px!important}';
  return '';
}
// characters per text line for the monospace templates (Courier New ≈ 0.6em per glyph)
function textCols(settings, tpl) {
  const paper58 = String(settings?.receipt_paper) === '58';
  const width = paper58 ? 181 : (String(settings?.receipt_print_width) === '64' ? 215 : 264);
  const font = tpl === 'senior' ? 16 : 11;
  return Math.max(20, Math.floor(width / (font * 0.6)));
}
// pre-formatted "label …… amount" line; label is clipped so the amount always sits on the same line
function tpad(left, right, cols, cls) {
  const l = String(left), r = String(right);
  const vis = (h) => h.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/&middot;/g, '·').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
  const lv = vis(l), rv = vis(r);
  let gap = cols - lv.length - rv.length;
  if (gap < 1) {
    // too long for one line → label on its own line, amount right-aligned on the next (never clip the label)
    const pad = Math.max(0, cols - rv.length);
    return `<div class="line pre${cls ? ' ' + cls : ''}">${l}</div><div class="line pre${cls ? ' ' + cls : ''}">${'&nbsp;'.repeat(pad)}${r}</div>`;
  }
  return `<div class="line pre${cls ? ' ' + cls : ''}">${l}${'&nbsp;'.repeat(gap)}${r}</div>`;
}

function designCss(settings) {
  const x = [templateCss(receiptTemplate(settings))];
  // pre-formatted text rows (compact/senior/a2): never wrap, size classes must not change the glyph width
  x.push('div.line.pre{white-space:pre;overflow:hidden;word-break:normal}div.line.pre.xl,div.line.pre.lg,div.line.pre.sub{font-size:inherit!important}');
  if (String(settings?.receipt_paper) === '58') x.push('@page{size:58mm auto!important}body{width:48mm!important;max-width:48mm!important}');
  if (settings?.receipt_font_size === 'large') x.push('body{font-size:14px!important}.lg{font-size:15px!important}');
  // 宽松行距 (Alfonzo 2026-10-31: "不喜欢紧凑，要和预览一模一样") — same 1.45 line-height + breathing room around separators as the doc-design preview
  if (settings?.receipt_airy && !settings?.receipt_compact_mode) x.push('body,table.r,table.r td,table.r3,table.r3 td,div.line,.sep,.xl,.lg,table.r.xl,table.r.lg{line-height:1.45!important}.sep{margin:3px 0}table.r td:last-child{padding-left:4px!important}');
  // 🖨️ 64mm printable head (BAZAR 2026-10-31: Gprinter GP-80160 clips the right edge). Keep the PAGE at full 80mm (a narrower custom page gets
  // centred/scaled by the Windows driver → clipped again) and push the content into the left 57mm with a wide right padding.
  if (String(settings?.receipt_print_width) === '64' && String(settings?.receipt_paper) !== '58') {
    x.push('body{width:79mm!important;max-width:79mm!important;padding:1mm 20.5mm 1mm 1.5mm!important;font-size:10.5px!important}table.r,table.r3{font-size:10.5px!important}table.r.xl,table.r.lg,.xl,.lg{font-size:11.5px!important}table.r td:first-child{width:auto!important}table.r td:last-child{width:1%!important}');
  }
  return x.join('');
}

function shopQrBlock(settings, order) {
  if (settings?.receipt_qr_enabled === false) return '';
    try {
    const u = JSON.parse(localStorage.getItem('cached_auth_user') || '{}');
    if (!u.tenant_id && !settings?.shop_slug) return '';
    const base = settings?.shop_slug
      ? `https://${settings.shop_slug}.sellox.shop`
      : `https://sellox.shop/shop/${u.tenant_id}`;
    // 🎰 one QR, many uses: ?o=<order_no> lets the customer spin the ruleta / rate the cashier
    const ono = order?.order_no && !/^\d{13}$/.test(String(order.order_no)) ? String(order.order_no) : '';
    const url = ono ? `${base}?o=${encodeURIComponent(ono)}` : base;
    const svg = qrPng(url);
    if (!svg) return '';
    const st = order?.stamps;
    const stampsLine = st && st.target ? `<div class="line center bold">Sellos: ${st.current}/${st.target}${st.can_redeem ? ' - PREMIO LISTO!' : ''}</div>` : '';
    return `<div class="center" style="margin-top:2mm">${svg}</div>` +
      `<div class="line light center">${ono ? 'Escanea: gira la ruleta / califica' : 'Escanea y pide online'}</div>` +
      stampsLine +
      `<div class="line light center" style="font-size:10px">${base.replace('https://', '')}</div>`;
  } catch { return ''; }
}

const SEPARATOR_CHAR = '=';
const SEPARATOR_COUNT = 48;

const CSS = `
@page { size: 80mm auto; margin: 0 !important; }
@media print { html, body { height: auto !important; overflow: visible !important; margin: 0 !important; padding: 0 !important; } }
* { margin: 0; padding: 0; box-sizing: border-box; page-break-inside: avoid; }
body {
  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
  font-weight: bold;
  line-height: 1.05;
  color: #000;
  background: #fff;
  padding: 1mm 3mm 1mm 1.5mm;
  width: 70mm;
  max-width: 70mm;
  -webkit-print-color-adjust: exact;
}
.center { text-align: center; }
.right { text-align: right; }
.xl { font-size: 13px; font-weight: bold; line-height: 1.1; }
.lg { font-size: 13px; font-weight: bold; }
.light { font-weight: bold; } /* LC 88 2026-11-03: normal weight printed too faint on thermal paper ("12 UND", ARTICULOS/UNIDADES) */
.sep {
  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
  font-weight: bold;
  line-height: 1.1;
  overflow: hidden;
  white-space: nowrap;
}
table.r {
  width: 100%;
  border-collapse: collapse;
  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
  font-weight: bold;
  line-height: 1.1;
}
table.r td {
  padding: 0;
  margin: 0;
  vertical-align: middle;
  font-family: 'Courier New', Courier, monospace;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
}
table.r td:first-child {
  text-align: left;
  width: 70%;
}
table.r td:last-child {
  text-align: right;
  width: 30%;
  white-space: nowrap;
}
table.r td.v { text-align: right; white-space: nowrap; }
table.r.ie { margin-bottom: 4px; }
table.r3 { width: 100%; border-collapse: collapse; font-family: 'Courier New', Courier, monospace; font-size: inherit; font-weight: bold; line-height: 1.1; }
table.r3 td { padding: 0; vertical-align: middle; white-space: nowrap; font-family: inherit; font-size: inherit; font-weight: inherit; }
table.r3 td.c1 { text-align: left; width: 40%; }
table.r3 td.c2 { text-align: right; width: 30%; font-weight: normal; }
table.r3 td.c3 { text-align: right; width: 30%; }
table.r3 td.cn { text-align: left; white-space: normal; }
table.r3.ie { margin-bottom: 4px; }
table.r3.hd td { font-weight: normal; }
.iname { font-family: 'Courier New', Courier, monospace; font-weight: bold; padding-left: 8px; margin-bottom: 3px; }
.iname .light { font-weight: bold; }
table.r.xl { font-size: 14px; }
table.r.lg { font-size: 13px; }
table.r.light { font-weight: bold; }
div.line {
  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
  font-weight: bold;
  line-height: 1.1;
  word-break: break-all;
}
div.line.light { font-weight: bold; }
`;

// Compact mode: same font sizes as normal, super-tight line spacing, hairline
// separator rules instead of the 32-char '=' bar. Saves ~50% paper.
// Activated when `settings.receipt_compact_mode === true` (per-tenant toggle).
const CSS_COMPACT = `
@page { size: 80mm auto; margin: 0 !important; }
@media print { html, body { height: auto !important; overflow: visible !important; margin: 0 !important; padding: 0 !important; } }
* { margin: 0 !important; padding: 0 !important; box-sizing: border-box; page-break-inside: avoid; }
body {
  font-family: 'Courier New', Courier, monospace;
  font-size: 15px;
  font-weight: bold;
  line-height: 0.95;
  color: #000;
  background: #fff;
  padding: 0 2mm !important;
  width: 72mm;
  max-width: 72mm;
  -webkit-print-color-adjust: exact;
}
.center { text-align: center; }
.right { text-align: right; }
.xl { font-size: 18px; font-weight: bold; line-height: 1.0; }
.lg { font-size: 16px; font-weight: bold; line-height: 1.0; }
.light { font-weight: bold; } /* LC 88 2026-11-03: normal weight printed too faint on thermal paper ("12 UND", ARTICULOS/UNIDADES) */
/* Hairline rule — replaces the heavy '====' bar. Text inside is hidden. */
.sep {
  height: 1px !important;
  font-size: 0 !important;
  line-height: 0 !important;
  overflow: hidden !important;
  border-bottom: 1px solid #999 !important;
  margin: 1px 0 !important;
}
table.r {
  width: 100%;
  border-collapse: collapse;
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
  font-weight: bold;
  line-height: 0.95;
}
table.r td {
  padding: 0 !important;
  margin: 0 !important;
  vertical-align: top;
  font-family: 'Courier New', Courier, monospace;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
}
table.r td.v { text-align: right; white-space: nowrap; }
table.r.ie { margin-bottom: 4px; }
table.r3 { width: 100%; border-collapse: collapse; font-family: 'Courier New', Courier, monospace; font-size: inherit; font-weight: bold; line-height: 1.1; }
table.r3 td { padding: 0; vertical-align: middle; white-space: nowrap; font-family: inherit; font-size: inherit; font-weight: inherit; }
table.r3 td.c1 { text-align: left; width: 40%; }
table.r3 td.c2 { text-align: right; width: 30%; font-weight: normal; }
table.r3 td.c3 { text-align: right; width: 30%; }
table.r3 td.cn { text-align: left; white-space: normal; }
table.r3.ie { margin-bottom: 4px; }
table.r3.hd td { font-weight: normal; }
.iname { font-family: 'Courier New', Courier, monospace; font-weight: bold; padding-left: 8px; margin-bottom: 3px; }
.iname .light { font-weight: bold; }
table.r.xl { font-size: 16px; }
table.r.lg { font-size: 14px; }
table.r.light { font-weight: bold; }
div.line {
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
  font-weight: bold;
  line-height: 0.95;
  word-break: break-all;
}
div.line.light { font-weight: bold; }
div.line.empty-line { display: none !important; }
`;

export function openPrint(body, opts) {
  const compact = !!(opts && opts.compact);
  const css = (compact ? CSS_COMPACT : CSS) + ((opts && opts.cssExtra) || '');
  const html = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Recibo</title><style>' + css + '</style></head><body>' + body + '</body></html>';
  if (opts && opts.returnHtml) return html; // doc-design live preview (no print)
  const popupPrint = () => {
    const w = window.open('', '_blank', 'width=320,height=400');
    if (!w) return false;
    w.document.open();
    w.document.write(html);
    w.document.close();
    setTimeout(() => {
      try { w.focus(); w.print(); } catch (e) { console.error('Print:', e); }
    }, 400);
    return true;
  };
  // 🧾 Generic/Text Only driver (migrated 1SISTEMA/a2 PCs): same HTML, sent as an ESC/POS raster image via RAW spooler
  if (rasterRawActive()) {
    printHtmlRaster(html).catch((e) => {
      busToast('error', '🖨️ Impresión directa falló / 直打失败', { description: String(e?.message || e), duration: 6000 });
      if (window.selloxDesktop?.printHTML) window.selloxDesktop.printHTML(html, (opts && opts.printerName) || undefined).catch(() => {});
    });
    return;
  }
  // Desktop app: ALWAYS print via IPC (hidden window, silent, default/named printer).
  // Never use a popup + window.print() here — on slow Win7 machines the silent-print
  // override injection loses the race and the OS printer dialog pops up (hugo 2026-08-25).
  if (typeof window !== 'undefined' && window.selloxDesktop?.printHTML) {
    window.selloxDesktop.printHTML(html, (opts && opts.printerName) || undefined)
      .then((r) => {
        // Silent print failed (no printer / offline / bad device) — tell the user
        // and fall back to a visible dialog instead of silently doing nothing.
        if (r && r.ok === false) {
          busToast('error', '🖨️ Impresión silenciosa falló / 静默打印失败', { description: (r.error || '') + ' — abriendo diálogo de impresión / 改用打印对话框', duration: 6000 });
          popupPrint();
        }
      })
      .catch(() => { popupPrint(); });
    return;
  }
  // Desktop app (very old shell without printHTML): popup silently intercepted by the shell
  if (typeof window !== 'undefined' && window.selloxDesktop) {
    if (popupPrint()) return;
  }
  let iframe = document.getElementById('receipt-print-frame');
  if (!iframe) {
    iframe = document.createElement('iframe');
    iframe.id = 'receipt-print-frame';
    iframe.style.cssText = 'position:fixed;left:-9999px;top:0;width:80mm;height:0;border:0;visibility:hidden;';
    document.body.appendChild(iframe);
  }
  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) { console.error('Print:', e); }
  }, 500);
}
// Format number as Venezuelan Bs (safe: handles undefined/NaN)
function fmtBs(amount) {
  const n = Number(amount);
  if (isNaN(n)) return '0,00';
  return n.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Shift to Venezuela wall-clock (UTC-4, no DST) so receipts print VE time
// even when the POS PC's Windows timezone is set to another country.
function toVE(d) { return new Date(d.getTime() - 4 * 3600 * 1000); }

// Format time: HH:MM a.m./p.m.
function fmtTime(d) {
  const v = toVE(d);
  let h = v.getUTCHours();
  const m = String(v.getUTCMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'p.m.' : 'a.m.';
  if (h > 12) h -= 12;
  if (h === 0) h = 12;
  return String(h).padStart(2, '0') + ':' + m + ' ' + ampm;
}

// Format time: HH:MM (24h, like SENIAT fiscal machine)
function fmtTime24(d) {
  const v = toVE(d);
  return String(v.getUTCHours()).padStart(2, '0') + ':' + String(v.getUTCMinutes()).padStart(2, '0') + ':' + String(v.getUTCSeconds()).padStart(2, '0');
}

// Format date: DD-MM-YYYY
function fmtDate(d) {
  const v = toVE(d);
  const dd = String(v.getUTCDate()).padStart(2, '0');
  const mm = String(v.getUTCMonth() + 1).padStart(2, '0');
  return dd + '-' + mm + '-' + v.getUTCFullYear();
}

// Safe toFixed helper
function safeFixed(val, digits = 2) {
  const n = Number(val);
  return isNaN(n) ? '0.00' : n.toFixed(digits);
}

// Escape HTML
function esc(s) { return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
export function cleanSpec(v) { const t = String(v || '').trim(); return /^(none|null|nan|n\/a|-|undefined)$/i.test(t) ? '' : t; }

// === HTML Builders ===

// Two-column row: left text aligned left, right text aligned right
function trow(left, right, cls) {
  const c = cls ? ` class="${cls}"` : '';
  return `<table class="r${cls ? ' ' + cls : ''}"><tr><td>${left}</td><td class="v">${right}</td></tr></table>`;
}

// Three-column item row: qty | unit price | total
function t3(a, b, c, cls) {
  return `<table class="r3${cls ? ' ' + cls : ''}"><tr><td class="c1">${a}</td><td class="c2">${b}</td><td class="c3">${c}</td></tr></table>`;
}

// Name (spans qty+unit cols) | total
// Centered text line
function tcenter(text, cls) {
  const c = cls || '';
  return `<div class="center ${c}">${text}</div>`;
}

// Full-width separator
function tsep() {
  return '<div class=sep>' + '-'.repeat(SEPARATOR_COUNT) + '</div>';
  return `<div class="sep">${SEPARATOR_CHAR.repeat(SEPARATOR_COUNT)}</div>`;
}

// Simple text line (left-aligned, full width)
function tline(text, cls) {
  const c = cls ? ` ${cls}` : '';
  return `<div class="line${c}">${text}</div>`;
}

// Empty line — hidden in compact mode (class="empty-line")
function tempty() {
  return '<div class="line empty-line">&nbsp;</div>';
}

// Cashea sales are billed by the registered Cashea merchant (single account
// shared by all branches) — receipt header shows that merchant, not the branch.
export function casheaBilling(order, s, settings) {
  const isCashea = order?.payment_method === 'cashea' ||
    (order?.payment_methods || []).some(p => (p.method || p.payment_method) === 'cashea');
  if (!isCashea || !settings?.cashea_receipt_name) return s;
  return {
    ...s,
    receipt_name: settings.cashea_receipt_name,
    rif: settings.cashea_receipt_rif || s.rif,
    receipt_address: settings.cashea_receipt_address || s.receipt_address,
    receipt_address2: settings.cashea_receipt_address ? '' : s.receipt_address2,
    receipt_phone: settings.cashea_receipt_phone || s.receipt_phone,
  };
}

/**
 * 80mm Receipt - Normal Sale
 * Matches Venezuelan thermal receipt format exactly
 */
export function printReceipt80mm(order, settings, exchangeRates, storeInfo, printOpts) {
  if (!order) return;
  const rate = exchangeRates?.sales_exchange_rate || exchangeRates?.usd_to_ves || 1;
  const now = order.date ? new Date(order.date) : new Date();

  // Store-specific receipt info, fallback to tenant settings
  const s = casheaBilling(order, storeInfo || {}, settings);
  const company = (s.receipt_name || settings?.company_name || settings?.shop_name || 'EMPRESA').toUpperCase();
  const rif = s.rif || settings?.company_tax_id || '';
  const addrLines = s.receipt_address
    ? [s.receipt_address, s.receipt_address2].filter(Boolean)
    : [settings?.company_address, settings?.company_address2, settings?.company_address3].filter(Boolean);

  const invoiceNo = order.fiscal_no || order.order_no || '00000000';
  const fecha = fmtDate(now);
  const horaShort = fmtTime24(now);
  const vendedor = order.cashier || '0001';
  const cliente = order.is_employee_credit && order.employee_name ? order.employee_name : (order.customer_name || order.customer_phone || '');
  const clienteRif = order.customer_rif || order.customer_tax_id || order.fiscal_rif || order.customer_ci || '';

  const payMap = { cash: 'EFECTIVO', cash_usd: `EFECTIVO ${curSym()}`, cash_bs: 'EFECTIVO Bs', card: 'TARJETA', punto: 'PUNTO', bio: 'BIO/PAGO MOVIL', transferencia: 'TRANSFERENCIA', transfer: 'TRANSFERENCIA', credit: 'CREDITO', mobile_pay: 'PAGO MOVIL', biopago: 'BIOPAGO', zelle: 'ZELLE', pagomovil: 'PAGO MOVIL', pago_movil: 'PAGO MOVIL' };
  const pago = payMap[order.payment_method] || (order.payment_method ? order.payment_method.toUpperCase() : 'EFECTIVO');
  const payInUsd = order.payment_currency === 'USD';

  const items = order.items || [];

  // Receipt currency mode: 'bs' (default, SENIAT style) | 'usd' | 'both'
  const tpl = receiptTemplate(settings);
  const isText = TEXT_TEMPLATES.includes(tpl);
  const cols = textCols(settings, tpl);
  const row = (l, r, cls) => (isText ? tpad(l, r, cols, cls) : trow(l, r, cls));
  const curMode = (tpl === 'senior' || tpl === 'a2') ? 'bs' : (settings?.receipt_currency_mode || 'bs'); // 大字老人版 / a2: Bs only
  const money = (bs) => {
    if (curMode === 'usd') return curSym() + safeFixed(bs / rate);
    if (curMode === 'both') return 'Bs ' + fmtBs(bs) + ` (${curSym()}` + safeFixed(bs / rate) + ')';
    return 'Bs ' + fmtBs(bs);
  };

  // E1_FISCAL_LAYOUT_V1: thermal ticket mirrors the SENIAT fiscal machine —
  // ALL item/total figures in Bs; only payment/change lines follow payment currency.
  const taxIncl = settings?.tax_included_in_price !== false;
  const taxGroups = {};
  let totalExentoBs = 0;
  let subtotalBs = 0;
  items.forEach(item => {
    const qty = item.quantity || 1;
    const amtUsd = item.amount || ((item.unit_price || 0) * qty);
    const amtBs = item.unit_price_bs ? item.unit_price_bs * qty * (1 - (item.discount || 0) / 100) : amtUsd * rate;
    const r = item.tax_rate ?? 0;
    if (r === 0) {
      totalExentoBs += amtBs;
    } else {
      if (!taxGroups[r]) taxGroups[r] = { base: 0, tax: 0 };
      const base = taxIncl ? amtBs / (1 + r / 100) : amtBs;
      taxGroups[r].base += base;
      taxGroups[r].tax += taxIncl ? amtBs - base : amtBs * r / 100;
    }
    subtotalBs += amtBs;
  });
  const totalTaxBs = Object.values(taxGroups).reduce((s2, g) => s2 + g.tax, 0);
  const promoUsd = parseFloat(order.promo_discount || 0);
  const promoBs = promoUsd > 0 ? promoUsd * rate : 0;
  // promo_in_items: lines already carry the promo price → show the saving, don't subtract twice
  const promoDeductBs = legacyPromoDeductBs(order, taxIncl ? subtotalBs : subtotalBs + totalTaxBs, rate);
  const ptsDiscUsd = parseFloat(order.points_discount || 0);
  const ptsBs = ptsDiscUsd > 0 ? ptsDiscUsd * rate : 0;
  const grandTotalBs = Math.max(0, (taxIncl ? subtotalBs : subtotalBs + totalTaxBs) - promoDeductBs - ptsBs);
  const grandTotalUsd = order.total_amount || 0;

  // Receipt is built as named segments so tenants can reorder/hide blocks (receipt_blocks)
  const SEG = { header: [], client: [], invoice_info: [], items: [], subtotal: [], tax_detail: [], total: [], payments: [], rate_line: [], mh_line: [], footer: [], qr: [] };
  let L = SEG.header;

  // Header — SENIAT fiscal machine layout (other templates: company name first, big; a2: RIF first like the old a2 ticket)
  if (tpl === 'fiscal') {
    // Owner decision 2026-11-02: keep the long-standing SENIAT-style header (tenants depend on it). Written warning given.
    L.push(tcenter('SENIAT', 'xl'));
    if (rif) L.push(tcenter('RIF ' + esc(rif)));
    if (company && company !== 'SENIAT') L.push(tcenter(esc(company)));
  } else if (tpl === 'a2') {
    if (rif) L.push(tcenter(esc(rif)));
    L.push(tcenter(esc(company)));
  } else {
    L.push(tcenter(esc(company), 'xl'));
    if (rif) L.push(tcenter('RIF ' + esc(rif)));
  }
  addrLines.forEach(a => L.push(tcenter(esc(a))));
  if (settings?.invoice_header) {
    String(settings.invoice_header).split('\n').filter(Boolean).forEach(ln => L.push(tcenter(esc(ln))));
  }
  L = SEG.client;
  if (tpl === 'a2') {
    L.push(tline('RAZON SOCIAL:' + esc(cliente)));
    L.push(tline('CI/RIF:' + esc(clienteRif)));
    if (order.customer_address) L.push(tline('Direccion:' + esc(order.customer_address)));
    L.push(tline('CAJA/Cajera:' + esc(vendedor)));
  } else {
  if (tpl === 'fiscal' || cliente || clienteRif) {
    if (tpl === 'fiscal') L.push(tline('*** DATOS DE CLIENTE ***'));
    if (tpl === 'fiscal' || cliente) L.push(tline((tpl === 'fiscal' ? 'R.SOCIAL: ' : 'CLIENTE: ') + esc(cliente)));
    if (tpl === 'fiscal' || clienteRif) L.push(tline('RIF/CI: ' + esc(clienteRif)));
  }
  if (cliente && order.customer_phone && cliente !== order.customer_phone) L.push(tline('TELEFONO: ' + esc(order.customer_phone)));
  const clienteDir = order.customer_address || '';
  if (clienteDir) L.push(tline('DIRECCION: ' + esc(clienteDir)));
  L.push(tline('VENDEDOR: ' + esc(vendedor)));
  }
  L = SEG.invoice_info;
  if (tpl === 'a2') {
    L.push(row('NO.', esc(String(invoiceNo))));
    L.push(row('FECHA:' + esc(fecha), 'HORA:' + esc(horaShort)));
    L.push(tsep());
  } else if (tpl !== 'fiscal') {
    L.push(row('N° ' + esc(String(invoiceNo)), esc(fecha) + ' ' + esc(horaShort), tpl === 'modern' ? 'sub' : ''));
    L.push(tsep());
  } else {
  L.push(tcenter('FACTURA'));
  if (String(invoiceNo).length > 12) {
    L.push(tline('FACTURA:'));
    L.push(row('', esc(String(invoiceNo))));
  } else {
    L.push(row('FACTURA:', esc(String(invoiceNo))));
  }
  L.push(row('FECHA: ' + esc(fecha), 'HORA: ' + esc(horaShort)));
  L.push(tsep());
  }

  // Items — header CANT | P.UNIT | TOTAL; row 1: "12x" | unit price | (blank); row 2: NAME (spans) | TOTAL
  // Bs·$ ("both") mode 2026-10-30: the dual amounts don't fit 3 columns on 80mm (owner's photo: "1xBs 1,246,00 ($1.40)Bs 1,246,00 ($1.40)"
  // crammed on one row) → print what the doc-design preview shows: one line per item "NAME (G) *qty …… Bs total ($total)".
  L = SEG.items;
  const oneLine = curMode === 'both';
  // Alfonzo 2026-11-01: P.UNIT and TOTAL collide on his XP-80C ("Bs 1.664,00Bs 1.664,00") → tenant toggle hides the unit-price column
  const hidePU = !!settings?.receipt_hide_punit;
  if (!oneLine && tpl === 'fiscal') L.push(hidePU ? trow('CANT', 'TOTAL', 'light') : t3('CANT', 'P.UNIT', 'TOTAL', 'hd'));
  if (tpl === 'modern') L.push(row('ARTÍCULO', 'TOTAL', 'hd'));
  items.forEach(item => {
    const qty = item.quantity || 1;
    const amtUsd = item.amount || ((item.unit_price || 0) * qty);
    const amtBs = item.unit_price_bs ? item.unit_price_bs * qty * (1 - (item.discount || 0) / 100) : amtUsd * rate;
    const ge = (item.tax_rate ?? 0) === 0 ? '(E)' : '(G)';
    const specStr = cleanSpec(item.product_spec || item.spec);
    const pcode = settings?.receipt_hide_code === false ? (item.product_code || item.code || item.sku_code || '') : ''; // owner 2026-11-01: barcode hidden on item lines for everyone unless the tenant turns it on
    const baseName = String(item.product_name || item.name || '');
    const specPart = specStr && !baseName.toUpperCase().includes(specStr.toUpperCase()) ? ' ' + specStr : '';
    const name = esc(((pcode ? pcode + ' ' : '') + baseName + specPart).toUpperCase());
    const isBox = item.price_mode === 'box' || item.unit === 'CAJA' || (item.boxes != null && item.boxes > 0);
    const fq = (n) => (Number.isInteger(n) ? String(n) : String(n).replace('.', ','));
    const lineTotal = settings?.receipt_items_bs_only ? 'Bs ' + fmtBs(amtBs) : money(amtBs);
    const qtyTxt = isBox ? `${fq(item.boxes != null ? item.boxes : qty)} CAJA` : fq(qty);
    if (tpl === 'a2') {
      // old a2 ticket: "10 x Bs 335,70" / "NOMBRE ………… Bs 3.357,00" / "## 1x12 ##" (units per box)
      L.push(tline(`${qtyTxt} x Bs ${fmtBs(amtBs / (qty || 1))}`));
      L.push(row(`${name}${ge === '(E)' ? '(E)' : ''}`, 'Bs ' + fmtBs(amtBs)));
      if (item.box_quantity > 1) L.push(tline(`## 1x${item.box_quantity} ##`));
    } else if (tpl === 'compact') {
      // one line: "3 x NOMBRE (G) …… total"
      L.push(row(`${qtyTxt}x ${name} ${ge}`, lineTotal));
    } else if (tpl === 'modern') {
      // bold name, second line "qty × unit" …… total
      L.push(tline(`<span class="name">${name}</span> <span class="sub">${ge}</span>`));
      L.push(row(`<span class="sub">${qtyTxt} × ${money(amtBs / (qty || 1))}</span>`, lineTotal));
    } else if (tpl === 'senior') {
      // big: "3 NOMBRE" then total right, Bs only
      L.push(tline(`${qtyTxt} ${name}`));
      L.push(row('', 'Bs ' + fmtBs(amtBs), 'lg'));
    } else if (oneLine) {
      // Alfonzo 2026-10-31: print EXACTLY what the doc-design preview shows — one line "NAME (G) *qty …… Bs total ($total)"; qty always visible
      const boxes = item.boxes != null ? item.boxes : qty;
      const units = item.boxes != null ? qty : qty * (item.box_quantity || 1);
      const q = isBox ? `*${fq(boxes)} CAJA <span class="light">&middot; ${units} UND</span>` : `*${fq(qty)}`;
      // Super Lucky (hugo) 2026-10-31: item lines in Bs only, the totals below keep "Bs (… $)"
      L.push(row(`${name} ${ge} ${q}`, settings?.receipt_items_bs_only ? 'Bs ' + fmtBs(amtBs) : money(amtBs)));
    } else if (isBox) {
      const boxes = item.boxes != null ? item.boxes : qty;
      const units = item.boxes != null ? qty : qty * (item.box_quantity || 1);
      const boxPriceBs = boxes > 0 ? amtBs / boxes : amtBs;
      L.push(hidePU ? trow(fq(boxes) + 'x CAJA', money(amtBs)) : t3(fq(boxes) + 'x CAJA', money(boxPriceBs), money(amtBs)));
      L.push(tline(name + ' ' + ge + ' <span class="light">&middot; ' + units + ' UND</span>'));
    } else {
      L.push(hidePU ? trow(fq(qty) + 'x', money(amtBs)) : t3(fq(qty) + 'x', money(amtBs / qty), money(amtBs)));
      L.push(tline(name + ' ' + ge));
    }
    if (item.combo_sub_items && item.combo_sub_items.length > 0) {
      item.combo_sub_items.forEach(sub => L.push(tline('&nbsp;&nbsp;&gt; ' + esc(sub))));
    }
    if (item.note) L.push(tline('&nbsp;&nbsp;* ' + esc(item.note)));
  });
  // 🧮 owner 2026-11-01: every template tells how many different products and how many units are on the ticket
  const nUnits = items.reduce((a, it) => a + (Number(it.quantity) || 0), 0);
  const nUnitsTxt = Number.isInteger(nUnits) ? String(nUnits) : nUnits.toFixed(2).replace('.', ',');
  if (tpl !== 'a2') L.push(row(`ARTICULOS: ${items.length}`, `UNIDADES: ${nUnitsTxt}`, 'light'));
  L.push(tsep());
  L = SEG.subtotal;
  if (tpl !== 'a2') {
  L.push(row('SUBTTL', money(subtotalBs)));
  if (promoDeductBs > 0) L.push(row('PROMO ' + esc(String(order.promo_names || '').toUpperCase().substring(0, 16)), '-' + money(promoDeductBs)));
  if (ptsBs > 0) L.push(row('PUNTOS (-' + (order.points_used || 0) + ' PTS)', '-' + money(ptsBs)));
  L.push(tsep());
  } else {
    if (promoDeductBs > 0) L.push(row('PROMO', '-' + money(promoDeductBs)));
    if (ptsBs > 0) L.push(row('PUNTOS (-' + (order.points_used || 0) + ' PTS)', '-' + money(ptsBs)));
  }
  L = SEG.tax_detail;
  if (tpl !== 'a2') {
  L.push(row('EXENTO (E)', money(totalExentoBs)));
  Object.entries(taxGroups).sort(([a], [b]) => Number(b) - Number(a)).forEach(([r, g]) => {
    const pct = Number(r).toFixed(2).replace('.', ',');
    L.push(row('BI G  (' + pct + '%)', money(g.base)));
    L.push(row('IVA G (' + pct + '%)', money(g.tax)));
  });
  }
  L = SEG.total;
  const _svcFee = parseFloat(order.service_fee || 0);
  if (_svcFee > 0) {
    const _svcPct = parseFloat(order.service_fee_pct || 0);
    L.push(row(_svcPct > 0 ? 'SERVICIO(' + _svcPct + '%)' : 'SERVICIO', money(_svcFee * rate)));
  }
  const igtfUsd = Number(order.igtf_amount) || 0;
  if (igtfUsd > 0) L.push(row('IGTF ' + (order.igtf_pct || 3) + '%', money(igtfUsd * rate)));
  if (tpl !== 'a2') L.push(tsep());
  if (tpl === 'a2') {
    L.push(row('TOTAL', 'Bs ' + fmtBs(grandTotalBs)));
  } else if (curMode === 'both') {
    L.push(row('TOTAL', money(grandTotalBs), 'lg')); // one line "Bs 1.280,00 ($7.50)" — matches the doc-design preview
  } else if (curMode === 'usd') {
    L.push(row('TOTAL', curSym() + safeFixed(grandTotalBs / rate), 'lg'));
  } else {
    L.push(row('TOTAL', 'Bs ' + fmtBs(grandTotalBs), 'lg'));
    // Providencia 00093 Art.8: operaciones en divisa muestran ambos montos + tipo de cambio
    L.push(row(`REF ${curSym()}`, curSym() + safeFixed(grandTotalBs / rate), 'light'));
  }
  if (promoBs > 0) L.push(tcenter('* USTED AHORRO Bs ' + fmtBs(promoSavedBs(order, promoDeductBs, rate)) + ' *'));
  if (ptsBs > 0) L.push(tcenter('* PUNTOS USADOS: ' + (order.points_used || 0) + ' = -Bs ' + fmtBs(ptsBs) + ' *'));
  if (parseFloat(order.points_earned || 0) > 0) L.push(tcenter('* GANO ' + order.points_earned + ' PUNTOS EN ESTA COMPRA *'));
  if (order.points_balance_after != null && order.points_balance_after > 0) L.push(tcenter('* PUNTOS ACUMULADOS: ' + order.points_balance_after + ' *'));

  // Payment lines
  L = SEG.payments;
  if (order.payment_methods && order.payment_methods.length > 0) {
    let totalPaidBs = 0;
    order.payment_methods.forEach(sp => {
      const mLabel = (sp.label || payMap[sp.method] || sp.method || 'OTRO').toUpperCase();
      const spAmt = parseFloat(sp.amount) || 0;
      if (sp.currency === 'USD') {
        const bsEq = sp.bs_equivalent ? parseFloat(sp.bs_equivalent) : spAmt * rate;
        totalPaidBs += bsEq;
        L.push(row(esc(mLabel), curSym() + safeFixed(spAmt)));
        L.push(row('', '= Bs ' + fmtBs(bsEq), 'light'));
      } else {
        totalPaidBs += spAmt;
        L.push(row(esc(mLabel), 'Bs ' + fmtBs(spAmt)));
      }
    });
    const splitChange = totalPaidBs - grandTotalBs;
    if (splitChange > 0.5) {
      L.push(row('RECIBIDO', 'Bs ' + fmtBs(totalPaidBs)));
      L.push(row('CAMBIO', 'Bs ' + fmtBs(splitChange), 'xl'));
    }
  } else if (payInUsd) {
    L.push(row(esc(pago) + ` ${curSym()}`, curSym() + safeFixed(grandTotalUsd)));
    L.push(row('', '= Bs ' + fmtBs(grandTotalBs), 'light'));
  } else {
    // Cash in Bs with change: EFECTIVO shows what the client handed over (doc-design preview: EFECTIVO 1.500 / CAMBIO 220)
    const _recBs = order.payment_method === 'cash' ? (order.received_amount || 0) : 0;
    L.push(row(esc(pago), 'Bs ' + fmtBs(_recBs > grandTotalBs + 0.5 ? _recBs : grandTotalBs)));
  }

  // Cash received / change (single payment method)
  if (order.payment_method === 'cash' && (!order.payment_methods || order.payment_methods.length === 0)) {
    const recAmt = order.received_amount || 0;
    const chgAmt = order.change_given;
    if (recAmt > 0) {
      if (payInUsd) {
        const cambioUsd = chgAmt != null ? chgAmt : recAmt - grandTotalUsd;
        if (cambioUsd > 0.01) {
          L.push(row('RECIBIDO', curSym() + safeFixed(recAmt)));
          if (order.change_currency === 'VES') {
            // 💵 Change handed over in bolívares (drawer view, 2026-08-25 Andy)
            L.push(row('VUELTO', 'Bs ' + fmtBs(cambioUsd * rate), 'xl'));
            L.push(row('', `= ${curSym()}` + safeFixed(cambioUsd), 'light'));
          } else {
            L.push(row('VUELTO', curSym() + safeFixed(cambioUsd), 'xl'));
            L.push(row('', '= Bs ' + fmtBs(cambioUsd * rate), 'light'));
          }
        }
      } else {
        const cambioBs = chgAmt != null ? chgAmt : recAmt - grandTotalBs;
        if (cambioBs > 0.5) {
          L.push(row('CAMBIO', 'Bs ' + fmtBs(cambioBs), 'xl'));
        }
      }
    }
  }

  if (tpl === 'a2') {
    // old a2 ticket always closes with CAMBIO + total units
    const _chg = order.payment_method === 'cash' && (!order.payment_methods || !order.payment_methods.length) ? Math.max(0, (order.change_given != null ? order.change_given : (order.received_amount || 0) - grandTotalBs)) : 0;
    if (!(_chg > 0.5)) L.push(row('CAMBIO', 'Bs 0,00'));
    L.push(tline('Articulos:' + items.length + '  Cantidad:' + nUnitsTxt));
  }

  L = SEG.rate_line;
  L.push(tcenter('Tasa BCV: 1 USD = Bs ' + fmtBs(rate), 'light'));
  if (order.order_note) L.push(tline('NOTA: ' + esc(order.order_note)));
  L = SEG.mh_line;
  if (order.control_no && !order.control_no_provisional) {
    // Official N° de Control (authorized imprenta digital series / SENIAT) — Providencia 00093 Art. 8
    L.push(row('N° DE CONTROL', esc(String(order.control_no))));
    const imp = order.imprenta || {};
    if (imp.name) L.push(tcenter(esc(`${imp.name}${imp.rif ? ' RIF ' + imp.rif : ''}${imp.auth_date ? ' · Aut. ' + imp.auth_date : ''}`), 'light'));
    L.push(tcenter('Emitida conforme a Providencia SNAT/2026/00093', 'light'));
  } else if (tpl !== 'a2') {
    // MH line as before. A machine serial is printed ONLY when this tenant really drives a certified fiscal machine;
    // a plain thermal printer never prints another machine's registration number (that is impersonation, not styling).
    const _fm = settings?.fiscal_print_method === 'fiscal_machine' || settings?.fiscal_print_method === 'fiscal_machine_aeg';
    L.push(row('MH', _fm ? esc(String(order.z_number || settings?.seniat_z_number || '')) : ''));
  }
  L = SEG.footer;
  if (settings?.invoice_footer) {
    String(settings.invoice_footer).split('\n').filter(Boolean).forEach(ln => L.push(tcenter(esc(ln))));
  }
  // 🎰 Gran Sorteo: the receipt prints only the ticket RANGE — the system is the real proof
  const _srt = order?.sorteo;
  if (_srt && _srt.count) {
    L.push(tcenter(`<b>🎟️ ${esc(_srt.campaign || 'GRAN SORTEO')}: ${_srt.count} BOLETO${_srt.count > 1 ? 'S' : ''}</b>`));
    L.push(tcenter(`<b>N° ${esc(_srt.first)}${_srt.count > 1 ? ` AL ${esc(_srt.last)}` : ''}</b>`));
    if (_srt.draw_date) L.push(tcenter('Sorteo: ' + esc(String(_srt.draw_date).slice(0, 10).split('-').reverse().join('/'))));
    L.push(tcenter('Registra con tu N° de boleto + N° de factura'));
  }
  L = SEG.qr;
  const _qr = shopQrBlock(settings, order);
  if (_qr) L.push(_qr);

  // 🧩 Assemble according to tenant's block design (order / visibility / custom text)
  const DEFAULT_BLOCK_ORDER = ['header', 'client', 'invoice_info', 'items', 'subtotal', 'tax_detail', 'total', 'payments', 'rate_line', 'mh_line', 'footer', 'qr'];
  const CORE_BLOCKS = ['items', 'subtotal', 'total', 'payments'];
  const cfg = (Array.isArray(settings?.receipt_blocks) && settings.receipt_blocks.length > 2)
    ? settings.receipt_blocks
    : DEFAULT_BLOCK_ORDER.map(id => ({ id, on: true }));
  const OUT = [];
  cfg.forEach(b => {
    if (!b || !b.id) return;
    if (b.type === 'text' || String(b.id).startsWith('txt_')) {
      if (b.on !== false && b.text) String(b.text).split('\n').filter(Boolean).forEach(ln => OUT.push(tcenter(esc(ln))));
      return;
    }
    if (b.on === false && !CORE_BLOCKS.includes(b.id)) return;
    (SEG[b.id] || []).forEach(x => OUT.push(x));
  });
  CORE_BLOCKS.forEach(id => { if (!cfg.some(b => b && b.id === id)) (SEG[id] || []).forEach(x => OUT.push(x)); });

  return openPrint(OUT.join('\n'), { compact: !!settings?.receipt_compact_mode, printerName: printOpts?.printerName, cssExtra: designCss(settings), returnHtml: !!printOpts?.returnHtml });
}

/**
 * Fiscal SENIAT Receipt
 * Exact match to Venezuelan SENIAT thermal printer output
 */
// SENIAT-compliant credit note (Providencia 0071): own consecutive number,
// reference to the affected invoice, Bs totals + BCV rate, IVA breakdown.
// 🧾 Fiscal X / Z summary on a normal 80mm thermal printer (stores without fiscal machine — LC 88, Alfonzo, Wu…). Owner 2026-11-02.
export function printFiscalReport80mm(report, kind, settings, storeInfo, printOpts) {
  const r = report || {};
  const isZ = kind === 'z';
  const rate = Number(r.total_sales) > 0 && Number(r.total_sales_bs) > 0 ? r.total_sales_bs / r.total_sales : 0;
  const money = (usd) => `${curSym()}${(Number(usd) || 0).toFixed(2)}`;
  // amount row = USD on the label line, Bs on a light line below (keeps labels on one line on 58/64/80 mm)
  // Bs-only tickets (fiscal-machine style, receipt_currency_mode 'bs') print every amount in Bs like the old Z; otherwise USD + Bs reference line
  const bsOnly = String(settings?.receipt_currency_mode || 'bs') === 'bs' && rate > 0;
  const amt = (label, usd, cls, neg) => {
    if (bsOnly) { L.push(trow(label, (neg ? '-' : '') + 'Bs ' + fmtBs((Number(usd) || 0) * rate), cls)); return; }
    L.push(trow(label, (neg ? '-' : '') + money(usd), cls)); if (rate > 0) L.push(trow('', (neg ? '-' : '') + 'Bs ' + fmtBs((Number(usd) || 0) * rate), 'light'));
  };
  const company = storeInfo?.receipt_name || storeInfo?.name || settings?.company_name || '';
  const rif = storeInfo?.rif || settings?.company_tax_id || '';
  const addr = [storeInfo?.receipt_address || settings?.company_address, storeInfo?.receipt_address2 || settings?.company_address2].filter(Boolean);
  const payMap = { cash: 'EFECTIVO', cash_usd: `EFECTIVO ${curSym()}`, cash_bs: 'EFECTIVO Bs', card: 'TARJETA', punto: 'PUNTO', bio: 'BIOPAGO', transfer: 'TRANSFERENCIA', transferencia: 'TRANSFERENCIA', credit: 'CREDITO', mobile_pay: 'PAGO MOVIL', pagomovil: 'PAGO MOVIL', pago_movil: 'PAGO MOVIL', zelle: 'ZELLE', mixed: 'MIXTO', cashea: 'CASHEA' };
  const L = [];
  L.push(tcenter(esc(company), 'xl'));
  if (rif) L.push(tcenter('RIF ' + esc(rif)));
  addr.forEach(a => L.push(tcenter(esc(a))));
  L.push(tsep());
  L.push(tcenter(isZ ? `CIERRE DIARIO N° ${esc(String(r.z_number ?? '').padStart(4, '0'))}` : 'REPORTE X (PARCIAL)', 'xl'));
  L.push(tcenter(isZ ? 'RESUMEN DE VENTAS DEL DIA' : 'VENTAS DEL DIA · NO CIERRA EL DIA', 'light'));
  L.push(trow('FECHA', esc(String(r.date || ''))));
  const gen = r.generated_at ? new Date(r.generated_at) : new Date();
  L.push(trow('HORA', esc(gen.toLocaleTimeString('es-VE', { hour12: false, timeZone: 'America/Caracas' }))));
  if (r.generated_by) L.push(trow('EMITIDO POR', esc(String(r.generated_by))));
  L.push(tsep());
  L.push(trow('OPERACIONES', String(r.total_orders ?? 0)));
  if (isZ && (r.first_invoice || r.last_invoice)) L.push(trow('FACTURAS', esc(`${r.first_invoice || '-'} a ${r.last_invoice || '-'}`)));
  L.push(tsep());
  // VENTAS block — same row names as the fiscal machine Z (EXENTO / BI G x% / IVA G x%)
  const pct = (k) => Number(k).toFixed(2).replace('.', ',') + '%';
  const ts = r.tax_summary || {};
  const rates = Object.keys(ts).sort((a, b) => Number(b) - Number(a));
  L.push(tline('VENTAS:', 'bold'));
  amt('EXENTO', r.total_exempt);
  rates.forEach(k => amt(`BI G ${pct(k)}`, (ts[k] || {}).base));
  rates.forEach(k => amt(`IVA G ${pct(k)}`, (ts[k] || {}).tax));
  L.push(tsep());
  amt('TTL VTA', r.total_sales, 'xl');
  amt('TTL IVA', r.total_tax);
  L.push(tsep());
  if (isZ) {
    // NOTAS DE CREDITO block (refunds of the day)
    L.push(tline('NOTAS DE CREDITOS:', 'bold'));
    amt('EXENTO', r.nc_exempt || 0);
    rates.forEach(k => amt(`BI G ${pct(k)}`, ((r.nc_tax_summary || {})[k] || {}).base || 0));
    rates.forEach(k => amt(`IVA G ${pct(k)}`, ((r.nc_tax_summary || {})[k] || {}).tax || 0));
    L.push(tsep());
    amt('NC. TTL VTA', r.total_refunds || 0);
    amt('NC. TTL IVA', r.nc_total_tax || 0);
    L.push(tsep());
  }
  if (Number(r.voided_count) > 0) amt(`ANULADOS (${r.voided_count})`, r.voided_amount);
  amt('VENTAS NETAS', isZ ? r.net_sales : r.net_total, 'xl');
  if (isZ) {
    // Counters block — mirrors ULT. FACTURA / #FACD / ULT. NF / #NFD / ULT. NC / #NCD of the fiscal machine
    L.push(tsep());
    L.push(trow('ULT. FACTURA', esc(String(r.last_invoice || '-'))));
    L.push(trow('#FACD', String(r.total_orders ?? 0)));
    L.push(trow('ULT. NF', esc(String(r.last_nf_no || '-'))));
    L.push(trow('#NFD', String(r.nf_count ?? 0)));
    L.push(trow('ULT. NC', esc(String(r.last_refund_no || '-'))));
    L.push(trow('#NCD', String(r.refund_count ?? 0)));
    if (r.accum_net_sales != null) { L.push(tsep()); amt('ACUMULADO VTAS', r.accum_net_sales); amt('ACUMULADO IVA', r.accum_tax); }
  }
  const bp = r.by_payment_method || {};
  if (Object.keys(bp).length) {
    L.push(tsep());
    L.push(tline('FORMAS DE PAGO'));
    Object.entries(bp).forEach(([k, v]) => {
      const amount = typeof v === 'object' ? v.amount : v;
      const cnt = typeof v === 'object' && v.count != null ? ` (${v.count})` : '';
      amt(esc((payMap[k] || String(k).toUpperCase()) + cnt), amount);
    });
  }
  if (rate > 0) { L.push(tsep()); L.push(tcenter('Tasa BCV: 1 USD = Bs ' + fmtBs(rate), 'light')); }
  L.push(tsep());
  if (isZ && (r.control_number_start || r.control_number_end)) L.push(trow('N° DE CONTROL', esc(`${r.control_number_start || '-'} a ${r.control_number_end || '-'}`)));
  L.push(tcenter(isZ ? 'CIERRE DIARIO · SELLOX POS · IMPRESORA NO FISCAL' : 'REPORTE X · NO CIERRA EL DIA', 'light'));
  L.push(tcenter('Documento de control interno · Prov. SNAT/2026/00093', 'light'));
  return openPrint(L.join("\n"), { compact: !!settings?.receipt_compact_mode, cssExtra: designCss(settings), printerName: settings?.pos_printer_name || undefined, returnHtml: !!printOpts?.returnHtml });
}


export function printCreditNote(refund, order, settings, exchangeRates, storeInfo) {
  const rate = exchangeRates?.sales_exchange_rate || exchangeRates?.usd_to_ves || 1;
  const now = refund.created_at ? new Date(refund.created_at) : new Date();
  const s = casheaBilling(order || {}, storeInfo || {}, settings);
  const company = (s.receipt_name || settings?.company_name || settings?.shop_name || 'EMPRESA').toUpperCase();
  const rif = s.rif || settings?.company_tax_id || '';
  const addrLines = s.receipt_address
    ? [s.receipt_address, s.receipt_address2].filter(Boolean)
    : [settings?.company_address, settings?.company_address2, settings?.company_address3].filter(Boolean);
  const phone = s.receipt_phone || settings?.company_phone || '';

  const items = refund.items || [];
  const totalUsd = refund.refund_amount || items.reduce((t, i) => t + (i.amount || 0), 0);
  const totalBs = totalUsd * rate;
  const incl = settings?.tax_included_in_price !== false;
  let ivaUsd = 0;
  items.forEach(i => {
    const r = i.tax_rate ?? 0;
    if (r > 0) {
      const amt = i.amount || ((i.unit_price || 0) * (i.quantity || 1));
      ivaUsd += incl ? amt - amt / (1 + r / 100) : amt * r / 100;
    }
  });
  const baseUsd = totalUsd - ivaUsd;

  const L = [];
  L.push(tcenter(esc(company), 'xl'));
  if (rif) L.push(tcenter('RIF: ' + esc(String(rif))));
  addrLines.forEach(a => L.push(tcenter(esc(String(a)), 'light')));
  if (phone) L.push(tcenter('TELF: ' + esc(String(phone)), 'light'));
  L.push(tsep());
  L.push(tcenter('*** NOTA DE CREDITO ***', 'xl'));
  L.push(trow('NOTA DE CREDITO Nº', esc(String(refund.refund_no || refund.id || ''))));
  L.push(trow('FECHA', fmtDate(now) + ' ' + fmtTime(now)));
  L.push(tsep());
  L.push(tline('AFECTA A FACTURA Nº: ' + esc(String(refund.original_order_no || order?.order_no || '')), 'bold'));
  const invDate = order?.created_at || order?.date;
  if (invDate) L.push(tline('FECHA FACTURA: ' + fmtDate(new Date(invDate))));
  if (order?.control_no && !order?.control_no_provisional) L.push(tline('N° CONTROL FACTURA: ' + esc(String(order.control_no))));
  if (order?.total_amount != null) L.push(tline('MONTO FACTURA: ' + curSym() + safeFixed(order.total_amount)));
  const cliente = order?.customer_name || refund.customer_name || '';
  const clienteRif = order?.customer_rif || order?.customer_tax_id || '';
  if (cliente) L.push(tline('CLIENTE: ' + esc(String(cliente))));
  if (clienteRif) L.push(tline('RIF/CI: ' + esc(String(clienteRif))));
  L.push(tsep());
  items.forEach(item => {
    const qty = item.quantity || 1;
    const up = item.unit_price || 0;
    const amt = item.amount || up * qty;
    L.push(tline(esc(String((item.product_name || item.product_id || '') + (item.product_spec ? ' ' + item.product_spec : '')))));
    L.push(trow(`  ${qty} x ${curSym()}${safeFixed(up)}`, `-${curSym()}` + safeFixed(amt)));
  });
  L.push(tsep());
  if (ivaUsd > 0.005) {
    L.push(trow('BASE IMPONIBLE', `-${curSym()}` + safeFixed(baseUsd)));
    L.push(trow('IVA', `-${curSym()}` + safeFixed(ivaUsd)));
  }
  L.push(trow('TOTAL NOTA CREDITO', `-${curSym()}` + safeFixed(totalUsd), 'xl'));
  L.push(trow('TOTAL Bs', '-Bs ' + fmtBs(totalBs), 'xl'));
  L.push(tcenter('Tasa BCV: 1 USD = Bs ' + fmtBs(rate), 'light'));
  if (refund.reason) L.push(tline('MOTIVO: ' + esc(String(refund.reason))));
  L.push(tempty());
  L.push(tcenter('Documento emitido segun', 'light'));
  L.push(tcenter('Providencia SNAT/2011/0071', 'light'));
  openPrint(L.join('\n'), { compact: !!settings?.receipt_compact_mode });
}

export function printFiscalReceipt(order, settings, exchangeRates, storeInfo, printOpts) {
  // Fiscal mode prints the exact SENIAT machine layout (Bs-only) — same as printReceipt80mm
  return printReceipt80mm(order, settings, exchangeRates, storeInfo, printOpts);
}
